import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { settingsService, SystemSetting, SettingsFilter } from '../services/settingsService';
import { integrationService, IntegrationInfo } from '../services/integrationService';
import { SettingCategory } from '../types/settings';

interface SettingsState {
  // Settings
  settings: SystemSetting[];
  settingsLoading: boolean;
  settingsError: string | null;
  categories: { category: SettingCategory; count: number }[];
  
  // Integrations
  integrations: IntegrationInfo[];
  integrationsLoading: boolean;
  integrationsError: string | null;
  
  // Actions
  fetchSettings: (filters?: SettingsFilter) => Promise<void>;
  fetchCategories: () => Promise<void>;
  updateSetting: (key: string, value: any) => Promise<void>;
  bulkUpdateSettings: (updates: Array<{ key: string; value: any }>) => Promise<void>;
  resetSetting: (key: string) => Promise<void>;
  
  // Integration actions
  fetchIntegrations: () => Promise<void>;
  connectIntegration: (id: string, config: any) => Promise<void>;
  disconnectIntegration: (id: string) => Promise<void>;
  syncIntegration: (id: string) => Promise<{ success: boolean; message: string }>;
  testIntegration: (id: string) => Promise<{ success: boolean; message: string }>;
  
  // Utility
  clearErrors: () => void;
}

export const useSettingsStore = create<SettingsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      settings: [],
      settingsLoading: false,
      settingsError: null,
      categories: [],
      integrations: [],
      integrationsLoading: false,
      integrationsError: null,

      // Settings actions
      fetchSettings: async (filters?: SettingsFilter) => {
        set({ settingsLoading: true, settingsError: null });
        try {
          const response = await settingsService.getSettings(filters);
          set({ settings: response.settings, settingsLoading: false });
        } catch (error: any) {
          set({ settingsError: error.message || 'Failed to fetch settings', settingsLoading: false });
        }
      },

      fetchCategories: async () => {
        try {
          const categories = await settingsService.getCategories();
          set({ categories });
        } catch (error: any) {
          console.error('Failed to fetch categories:', error);
        }
      },

      updateSetting: async (key: string, value: any) => {
        set({ settingsLoading: true, settingsError: null });
        try {
          const updatedSetting = await settingsService.updateSetting(key, value);
          
          // Update local state
          set(state => ({
            settings: state.settings.map(s => 
              s.settingKey === key ? updatedSetting : s
            ),
            settingsLoading: false,
          }));
        } catch (error: any) {
          set({ settingsError: error.message || 'Failed to update setting', settingsLoading: false });
          throw error;
        }
      },

      bulkUpdateSettings: async (updates: Array<{ key: string; value: any }>) => {
        set({ settingsLoading: true, settingsError: null });
        try {
          const updatedSettings = await settingsService.bulkUpdateSettings(updates);
          
          // Update local state
          set(state => {
            const updateMap = new Map(updatedSettings.map(s => [s.settingKey, s]));
            return {
              settings: state.settings.map(s => 
                updateMap.has(s.settingKey) ? updateMap.get(s.settingKey)! : s
              ),
              settingsLoading: false,
            };
          });
        } catch (error: any) {
          set({ settingsError: error.message || 'Failed to update settings', settingsLoading: false });
          throw error;
        }
      },

      resetSetting: async (key: string) => {
        set({ settingsLoading: true, settingsError: null });
        try {
          const resetSetting = await settingsService.resetSetting(key);
          
          // Update local state
          set(state => ({
            settings: state.settings.map(s => 
              s.settingKey === key ? resetSetting : s
            ),
            settingsLoading: false,
          }));
        } catch (error: any) {
          set({ settingsError: error.message || 'Failed to reset setting', settingsLoading: false });
          throw error;
        }
      },

      // Integration actions
      fetchIntegrations: async () => {
        set({ integrationsLoading: true, integrationsError: null });
        try {
          const integrations = await integrationService.getIntegrations();
          set({ integrations, integrationsLoading: false });
        } catch (error: any) {
          set({ integrationsError: error.message || 'Failed to fetch integrations', integrationsLoading: false });
        }
      },

      connectIntegration: async (id: string, config: any) => {
        set({ integrationsLoading: true, integrationsError: null });
        try {
          const updated = await integrationService.connectIntegration(id, config);
          
          // Update local state
          set(state => ({
            integrations: state.integrations.map(i => 
              i.id === id ? updated : i
            ),
            integrationsLoading: false,
          }));
        } catch (error: any) {
          set({ integrationsError: error.message || 'Failed to connect integration', integrationsLoading: false });
          throw error;
        }
      },

      disconnectIntegration: async (id: string) => {
        set({ integrationsLoading: true, integrationsError: null });
        try {
          const updated = await integrationService.disconnectIntegration(id);
          
          // Update local state
          set(state => ({
            integrations: state.integrations.map(i => 
              i.id === id ? updated : i
            ),
            integrationsLoading: false,
          }));
        } catch (error: any) {
          set({ integrationsError: error.message || 'Failed to disconnect integration', integrationsLoading: false });
          throw error;
        }
      },

      syncIntegration: async (id: string) => {
        try {
          const result = await integrationService.syncIntegration(id);
          
          // Refresh integration status
          const updated = await integrationService.getIntegration(id);
          set(state => ({
            integrations: state.integrations.map(i => 
              i.id === id ? updated : i
            ),
          }));
          
          return result;
        } catch (error: any) {
          throw error;
        }
      },

      testIntegration: async (id: string) => {
        try {
          return await integrationService.testConnection(id);
        } catch (error: any) {
          throw error;
        }
      },

      clearErrors: () => {
        set({ settingsError: null, integrationsError: null });
      },
    }),
    {
      name: 'settings-store',
    }
  )
);